import { getExtension } from './uploadValidation';

export function normalizeDisplayName(name) {
  return String(name || '').trim().toLowerCase();
}

export function coerceRecordValue(value) {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => coerceRecordValue(item))
      .filter((item) => String(item).trim() !== '')
      .join(' ');
  }

  if (typeof value === 'object') {
    const nestedFields = [
      'status',
      'Status',
      'scanStatus',
      'ScanStatus',
      'result',
      'Result',
      'scanResult',
      'ScanResult',
      'verdict',
      'Verdict',
      'code',
      'Code',
      'name',
      'Name',
      'value',
      'Value',
      'message',
      'Message'
    ];

    for (const nestedField of nestedFields) {
      if (Object.prototype.hasOwnProperty.call(value, nestedField)) {
        const nestedValue = coerceRecordValue(value[nestedField]);
        if (String(nestedValue).trim() !== '') {
          return nestedValue;
        }
      }
    }

    return Object.entries(value)
      .map(([, entryValue]) => coerceRecordValue(entryValue))
      .filter((item) => String(item).trim() !== '')
      .join(' ');
  }

  return String(value);
}

export function getRecordFieldValue(record, fieldName) {
  if (!record || !fieldName) {
    return '';
  }

  if (Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return coerceRecordValue(record[fieldName]);
  }

  const target = String(fieldName).toLowerCase();
  const matchedKey = Object.keys(record).find((key) => String(key).toLowerCase() === target);
  return matchedKey ? coerceRecordValue(record[matchedKey]) : '';
}

export function getFirstRecordValue(record, fieldNames) {
  if (!record) {
    return '';
  }

  for (const fieldName of fieldNames) {
    const value = getRecordFieldValue(record, fieldName);
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }

  return '';
}

export function getRawRecordFieldValue(record, fieldName) {
  if (!record || !fieldName) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return record[fieldName];
  }

  const target = String(fieldName).toLowerCase();
  const matchedKey = Object.keys(record).find((key) => String(key).toLowerCase() === target);
  return matchedKey ? record[matchedKey] : undefined;
}

export function getFirstRawRecordValue(record, fieldNames) {
  if (!record) {
    return undefined;
  }

  for (const fieldName of fieldNames ?? []) {
    const value = getRawRecordFieldValue(record, fieldName);
    if (value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '')) {
      return value;
    }
  }

  return undefined;
}

export function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function copyNonEmptyRecordFields(target, source) {
  if (!isPlainObject(source)) {
    return target;
  }

  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return;
    }

    target[key] = value;
  });

  return target;
}

export function getRecordMediaId(record) {
  return getFirstRecordValue(record, ['mediaId', 'MediaId', 'mediaID', 'MediaID']);
}

export function getRecordTranscriptFileId(record) {
  return getFirstRecordValue(record, ['transcriptFileId', 'TranscriptFileId', 'transcriptId', 'TranscriptId']);
}

export function getRecordSubmittedAtUtc(record) {
  return getFirstRecordValue(record, ['submittedAtUtc', 'SubmittedAtUtc']);
}

export function getRecordFileType(record) {
  return getFirstRecordValue(record, [
    'fileType',
    'FileType',
    'contentTypeGroup',
    'ContentTypeGroup',
    'documentType',
    'DocumentType',
    'recordType',
    'RecordType',
    'fileCategory',
    'FileCategory',
    'category',
    'Category',
    'type',
    'Type'
  ]);
}

export function getRecordSizeInBytes(record) {
  const rawSize = getFirstRecordValue(record, [
    'fileSizeInBytes',
    'FileSizeInBytes',
    'fileSizeBytes',
    'FileSizeBytes',
    'sizeInBytes',
    'SizeInBytes',
    'contentLength',
    'ContentLength',
    'length',
    'Length',
    'fileSize',
    'FileSize',
    'size',
    'Size'
  ]);
  const size = Number(rawSize);
  return Number.isFinite(size) && size > 0 ? size : 0;
}

export function inferRecordExtension(record) {
  const name = getFirstRecordValue(record, [
    'storedFileName',
    'StoredFileName',
    'fileName',
    'FileName',
    'name',
    'Name',
    'originalFileName',
    'OriginalFileName',
    'displayFileName',
    'DisplayFileName'
  ]);
  return getExtension(String(name || ''));
}

export function getRecordMimeType(record) {
  return normalizeDisplayName(getFirstRecordValue(record, [
    'mimeType',
    'MimeType',
    'contentType',
    'ContentType'
  ]));
}

export function getRecordDisplayStatus(record, getRecordScanStatus, getRecordLifecycleStatus) {
  const scanStatus = getRecordScanStatus(record);
  if (scanStatus) {
    return scanStatus;
  }

  return getRecordLifecycleStatus(record) || 'Unknown';
}

export function getRecordLogicalName(record) {
  return normalizeDisplayName(getFirstRecordValue(record, [
    'storedFileName',
    'StoredFileName',
    'fileName',
    'FileName',
    'name',
    'Name',
    'originalFileName',
    'OriginalFileName',
    'displayFileName',
    'DisplayFileName'
  ]));
}

export function getRecordStoredFileName(record) {
  return normalizeDisplayName(getFirstRecordValue(record, [
    'storedFileName',
    'StoredFileName'
  ]));
}

export function getRecordOriginalFileName(record) {
  return normalizeDisplayName(getFirstRecordValue(record, [
    'originalFileName',
    'OriginalFileName',
    'fileName',
    'FileName',
    'name',
    'Name'
  ]));
}

export function getRecordObjectKey(record) {
  return normalizeDisplayName(getFirstRecordValue(record, [
    'objectKey',
    's3Key',
    'storageKey',
    'blobKey',
    'fileKey',
    'key'
  ]));
}

export function getRecordNameCandidates(record) {
  const names = [
    getFirstRecordValue(record, ['storedFileName', 'StoredFileName']),
    getFirstRecordValue(record, ['fileName', 'FileName']),
    getFirstRecordValue(record, ['name', 'Name']),
    getFirstRecordValue(record, ['originalFileName', 'OriginalFileName']),
    getFirstRecordValue(record, ['displayFileName', 'DisplayFileName'])
  ]
    .map((value) => normalizeDisplayName(value))
    .filter((value) => Boolean(value));

  const expanded = new Set(names);
  names.forEach((name) => {
    expanded.add(name.replace(/^media-/, ''));
    expanded.add(name.replace(/^transcript-/, ''));
  });

  return Array.from(expanded.values()).filter((value) => Boolean(value));
}
